/**
 * InboxTile - Shows unprocessed emails (not handled, not internal, not external)
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { Panel, Badge, Spinner } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY } from '../../design/tokens';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { apiClient } from '../../api/apiConfig';
import EmailDetailModal from '../EmailDetailModal';

interface InboxEmail {
  id: number;
  sender_name: string;
  sender: string;
  subject: string;
  received_at: string;
  summary?: string;
}

interface FullEmail extends InboxEmail {
  body: string;
  html_body?: string;
  recipients: string;
  is_handled?: boolean;
}

interface InboxTileProps {
  emails: InboxEmail[];
  loading?: boolean;
  onEmailHandled?: () => void;
}

const InboxTile: React.FC<InboxTileProps> = ({ emails, loading = false, onEmailHandled }) => {
  const [selectedEmail, setSelectedEmail] = useState<FullEmail | null>(null);
  const [loadingEmail, setLoadingEmail] = useState(false);

  const handleEmailClick = async (emailId: number) => {
    setLoadingEmail(true);
    try {
      const res = await apiClient.get(`/api/emails/${emailId}/`);
      setSelectedEmail(res.data);
      // Mark as read
      await apiClient.post(`/api/emails/${emailId}/mark_read/`).catch(() => {});
    } catch (err) {
      console.error('Failed to fetch email:', err);
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleMarkHandled = async (emailId: number) => {
    try {
      await apiClient.post(`/api/emails/${emailId}/mark_handled/`);
      setSelectedEmail(null);
      onEmailHandled?.();
    } catch (err) {
      console.error('Failed to mark handled:', err);
    }
  };

  const handleBlockSender = async (emailId: number, blockType: 'email' | 'domain') => {
    try {
      await apiClient.post(`/api/emails/${emailId}/block_sender/`, { block_type: blockType });
      setSelectedEmail(null);
      onEmailHandled?.();
    } catch (err) {
      console.error('Failed to block sender:', err);
    }
  };

  const handleMarkNewsletter = async (emailId: number) => {
    try {
      await apiClient.post(`/api/emails/${emailId}/mark_newsletter/`);
      setSelectedEmail(null);
      onEmailHandled?.();
    } catch (err) {
      console.error('Failed to mark newsletter:', err);
    }
  };

  if (loading) {
    return (
      <Panel style={{ minHeight: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size="md" />
      </Panel>
    );
  }

  return (
    <>
      <Panel>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[12] }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
            <EnvelopeIcon style={{ width: '18px', height: '18px', color: COLORS.accent.default }} />
            <span style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h3,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              Inbox
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
            {emails.length > 0 && (
              <Badge variant="warning" size="sm">{emails.length}</Badge>
            )}
            <Link to="/emails" style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.accent.default, textDecoration: 'none' }}>
              View All
            </Link>
          </div>
        </div>

        {emails.length === 0 ? (
          <div style={{ color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.body.sizes.default, textAlign: 'center', padding: SPACING[8] }}>
            Inbox zero
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
            {emails.slice(0, 8).map((email) => (
              <div
                key={email.id}
                onClick={() => handleEmailClick(email.id)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: SPACING[0],
                  padding: `${SPACING[4]}px 0`,
                  borderBottom: `1px solid ${COLORS.bg.hover}`,
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{
                    fontSize: TYPOGRAPHY.body.sizes.small,
                    fontWeight: '600',
                    color: COLORS.text.primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '60%',
                  }}>
                    {email.sender_name || email.sender}
                  </span>
                  <span style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary, flexShrink: 0 }}>
                    {formatDistanceToNow(parseISO(email.received_at), { addSuffix: true })}
                  </span>
                </div>
                <span style={{
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  color: COLORS.text.secondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {email.subject}
                </span>
              </div>
            ))}
            {emails.length > 8 && (
              <Link to="/emails" style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.accent.default, textAlign: 'center', display: 'block', textDecoration: 'none' }}>
                +{emails.length - 8} more
              </Link>
            )}
          </div>
        )}
      </Panel>

      {/* Email Detail Modal - shared component */}
      <EmailDetailModal
        email={selectedEmail}
        loading={loadingEmail}
        onClose={() => setSelectedEmail(null)}
        onMarkHandled={handleMarkHandled}
        onBlockSender={handleBlockSender}
        onMarkNewsletter={handleMarkNewsletter}
      />
    </>
  );
};

export default InboxTile;
